/**
 * UI Elements store for managing UI element templates
 *
 * This store handles caching and syncing of UI elements for games,
 * using the GenericStore pattern for persistent caching with local-first approach.
 */

import { storeRegistry } from "@/rerun-manager/GenericStore";
import { apiService } from "@/lib/api-functions/api-service";

// UI Element interface matching the API response
export interface UIElement {
  id: string;
  game_id: string;
  name: string;
  html: string;
  css: string;
  tags: string[];
  status?: 'draft' | 'published';
  created_at: string;
  updated_at: string;
  created_by?: string;
}

/**
 * Store for UI elements, using GenericStore
 */
export const uiElementsStore = storeRegistry.getStore<UIElement>({
  key: "ui_elements",
  ttlMs: 1000 * 60 * 30, // 30 minutes cache
});

/**
 * Fetch UI elements with local-first approach
 * Returns cached data immediately if available, then syncs with server
 * @param gameId - The game ID to fetch UI elements for
 * @param toast - Optional toast function for error reporting
 */
export async function fetchUIElements(
  gameId: string,
  toast?: (props: {
    title?: React.ReactNode;
    description?: React.ReactNode;
    variant?: string;
  }) => void
): Promise<UIElement[]> {
  try {
    // First, try to get cached data for immediate response
    const cachedData = getCachedUIElements(gameId);
    
    // If we have cached data, return it immediately and sync in background
    if (cachedData.length > 0) {
      // Start background sync but don't wait for it
      syncUIElementsInBackground(gameId, toast);
      return cachedData;
    }

    // If no cached data, fetch from server
    return await fetchUIElementsFromServer(gameId, toast);
  } catch (err) {
    const message =
      err instanceof Error ? err.message : "Failed to fetch UI elements";
    console.error("uiElementsStore: Error fetching UI elements:", err);
    if (toast) {
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
    throw new Error(message);
  }
}

/**
 * Fetch UI elements from server and update store
 */
async function fetchUIElementsFromServer(
  gameId: string,
  toast?: (props: {
    title?: React.ReactNode;
    description?: React.ReactNode;
    variant?: string;
  }) => void
): Promise<UIElement[]> {
  // Get the auth token
  const token = await apiService.getToken();
  if (!token) {
    console.error("uiElementsStore: No authentication token available");
    throw new Error("No authentication token available");
  }

  // Fetch data from API
  const result = await apiService.fetchUIElements(gameId);

  if (!result.success || !result.data) {
    console.error("uiElementsStore: API call failed:", result.error);
    throw new Error(result.error || "Failed to fetch UI elements");
  }

  const uiElements = result.data;

  // Sync the store with the fetched data
  await uiElementsStore.syncFromRemote(async () => uiElements);

  return uiElements;
}

/**
 * Background sync with server - doesn't throw errors to avoid interrupting UI
 */
async function syncUIElementsInBackground(
  gameId: string,
  toast?: (props: {
    title?: React.ReactNode;
    description?: React.ReactNode;
    variant?: string;
  }) => void
): Promise<void> {
  try {
    await fetchUIElementsFromServer(gameId);
  } catch (error) {
    console.warn("Background sync failed:", error);
    // Don't show toast for background sync failures to avoid disrupting user experience
  }
}

/**
 * Create a new UI element via API and sync the store
 * @param item - The UI element data to create
 * @param toast - Optional toast function for error reporting
 */
export async function createUIElement(
  item: {
    game_id: string;
    name: string;
    html: string;
    css?: string;
    tags?: string[];
    status?: 'draft' | 'published';
  },
  toast?: (props: {
    title?: React.ReactNode;
    description?: React.ReactNode;
    variant?: string;
  }) => void
): Promise<UIElement> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error("No authentication token available");
    }

    // Create data via API
    const result = await apiService.createUIElement(item);

    if (!result.success || !result.data) {
      throw new Error(result.error || "Failed to create UI element");
    }

    // Add the new item to local store
    const currentData = uiElementsStore.getData();
    const newData = [...currentData, result.data];
    uiElementsStore.setData(newData);

    return result.data;
  } catch (err) {
    const message =
      err instanceof Error ? err.message : "Failed to create UI element";
    console.error("createUIElement - error:", err);
    if (toast) {
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
    throw new Error(message);
  }
}

/**
 * Update an existing UI element with local-first approach
 * Updates local store immediately (optimistic update), then syncs to server
 * @param item - The UI element data to update
 * @param toast - Optional toast function for error reporting
 */
export async function updateUIElement(
  item: {
    id: string;
    game_id: string;
    name: string;
    html: string;
    css?: string;
    tags?: string[];
    status?: 'draft' | 'published';
  },
  toast?: (props: {
    title?: React.ReactNode;
    description?: React.ReactNode;
    variant?: string;
  }) => void
): Promise<UIElement> {
  // Store the current state for potential rollback
  const currentData = uiElementsStore.getData();
  const existingItemIndex = currentData.findIndex(el => el.id === item.id);
  
  if (existingItemIndex === -1) {
    throw new Error("UI element not found in local store");
  }
  
  // Create the updated item with current timestamp (optimistic update)
  const updatedItem: UIElement = {
    ...currentData[existingItemIndex],
    ...item,
    updated_at: new Date().toISOString(),
  };
  
  // Update local store immediately for instant UI feedback
  const optimisticData = currentData.map((el) =>
    el.id === item.id ? updatedItem : el
  );
  uiElementsStore.setData(optimisticData);

  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error("No authentication token available");
    }

    // Update data via API
    const result = await apiService.updateUIElement(item);

    if (!result.success || !result.data) {
      throw new Error(result.error || "Failed to update UI element");
    }

    // Update the store with the server response (in case server modified anything)
    const serverUpdatedItem = result.data;
    const finalData = uiElementsStore.getData().map((el) =>
      el.id === item.id ? serverUpdatedItem : el
    );
    uiElementsStore.setData(finalData);

    return serverUpdatedItem;
  } catch (err) {
    // Revert the optimistic update on error
    uiElementsStore.setData(currentData);
    
    const message =
      err instanceof Error ? err.message : "Failed to update UI element";
    console.error("updateUIElement - error:", err);
    if (toast) {
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
    throw new Error(message);
  }
}

/**
 * Delete a UI element and sync the store
 * @param id - The UI element ID to delete
 * @param gameId - The game ID
 * @param toast - Optional toast function for error reporting
 */
export async function deleteUIElement(
  id: string,
  gameId: string,
  toast?: (props: {
    title?: React.ReactNode;
    description?: React.ReactNode;
    variant?: string;
  }) => void
): Promise<void> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error("No authentication token available");
    }

    // Delete data via API
    const result = await apiService.deleteUIElement(id, gameId);

    if (!result.success) {
      throw new Error(result.error || "Failed to delete UI element");
    }

    // Remove the item from local store
    const currentData = uiElementsStore.getData();
    const newData = currentData.filter((el) => el.id !== id);
    uiElementsStore.setData(newData);

  } catch (err) {
    const message =
      err instanceof Error ? err.message : "Failed to delete UI element";
    console.error("deleteUIElement - error:", err);
    if (toast) {
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
    throw new Error(message);
  }
}

/**
 * Get cached UI elements for a specific game
 * @param gameId - The game ID
 * @returns Cached UI elements for the game, or empty array if none cached
 */
export function getCachedUIElements(gameId: string): UIElement[] {
  const allData = uiElementsStore.getData();
  return allData.filter((el) => el.game_id === gameId);
}

/**
 * Clear the UI elements cache
 */
export function clearUIElementsCache(): void {
  uiElementsStore.setData([]);
}

